import {
  AuthorizationEvent,
  AuthorizationPersonType,
  AuthorizationStatus,
  CreditAccountView,
  CreditAuthorization,
  CreditHistoryQuery,
  CreditHistoryView,
} from '@data/interfaces/credit-history.interface'

/**
 * Rótulos del historial crediticio (plan v4.2 §9.1): los comparten la pestaña «Buró y Círculo» de la ficha y la bandeja
 * de validaciones de Legal. La API manda claves; aquí se dicen en español.
 */

type TagSeverity = 'success' | 'info' | 'warning' | 'danger' | 'secondary'

export const STATUS_UI: Readonly<Record<AuthorizationStatus, { label: string; severity: TagSeverity; icon: string }>> = {
  pending: { label: 'Pendiente de Legal', severity: 'warning', icon: 'pi pi-clock' },
  approved: { label: 'Aprobada', severity: 'success', icon: 'pi pi-check-circle' },
  rejected: { label: 'Rechazada', severity: 'danger', icon: 'pi pi-times-circle' },
  expired: { label: 'Vencida', severity: 'secondary', icon: 'pi pi-calendar-times' },
  superseded: { label: 'Reemplazada', severity: 'secondary', icon: 'pi pi-replay' },
}

export const ACTION_LABELS: Readonly<Record<AuthorizationEvent['action'], string>> = {
  created: 'Mandada a validación',
  approved: 'Aprobada',
  rejected: 'Rechazada',
  expired: 'Venció',
  superseded: 'Reemplazada por una más reciente',
}

export const PERSON_TYPE_LABELS: Readonly<Record<AuthorizationPersonType, string>> = {
  PF: 'Persona física',
  PFAE: 'Persona física con actividad empresarial',
  PM: 'Persona moral',
}

export const TRAY_STATUSES: ReadonlyArray<{ value: AuthorizationStatus; label: string }> = [
  { value: 'pending', label: 'Pendientes' },
  { value: 'approved', label: 'Aprobadas' },
  { value: 'rejected', label: 'Rechazadas' },
  { value: 'expired', label: 'Vencidas' },
]

/** Una aprobada que ya pasó su fecha se trata como vencida aunque el cron todavía no la haya marcado. */
export function effectiveStatus(authorization: Pick<CreditAuthorization, 'status' | 'validUntil'>, now: Date = new Date()): AuthorizationStatus {
  if (authorization.status === 'approved' && authorization.validUntil && new Date(authorization.validUntil) <= now) return 'expired'
  return authorization.status
}

// ── Consultas a Círculo (E5b) ─────────────────────────────────────────────────

/** Cómo se ve cada consulta en el historial: el resultado, no el estado interno. */
export function queryResultUi(query: Pick<CreditHistoryQuery, 'status' | 'outcome'>): { label: string; severity: TagSeverity; icon: string } {
  if (query.status === 'requesting') return { label: 'En curso', severity: 'info', icon: 'pi pi-spin pi-spinner' }
  if (query.status === 'failed') return { label: 'Error', severity: 'danger', icon: 'pi pi-times-circle' }
  if (query.status === 'reused') return { label: 'Reutilizada', severity: 'info', icon: 'pi pi-replay' }
  return query.outcome === 'found' ? { label: 'Con historial', severity: 'success', icon: 'pi pi-check-circle' } : { label: 'Sin registros', severity: 'warning', icon: 'pi pi-search' }
}

//* El botón «Consultar historial» (plan v4.2 §9.2). `hidden`: la consulta está apagada o el usuario no es Legal.
//* `in_progress` le gana a todo: mientras haya una en curso no se manda otra. `disabled`: sin formato aprobado y
//* vigente, o con datos que Círculo rechazaría. `reuse`: hay una encontrada dentro de la ventana. `with_warning`: la
//* última no recibió respuesta y pudo cobrarse. `query`: lo demás.
export type QueryButtonState = 'hidden' | 'in_progress' | 'disabled' | 'reuse' | 'with_warning' | 'query'

export function queryButtonState(view: Pick<CreditHistoryView, 'current' | 'queries' | 'circulo'>, canQuery: boolean, now: Date = new Date()): { state: QueryButtonState; reasons: string[] } {
  if (!canQuery || !view.circulo?.enabled) return { state: 'hidden', reasons: [] }
  const latest = view.queries[0] ?? null
  if (latest?.status === 'requesting') return { state: 'in_progress', reasons: [] }
  if (!view.current || effectiveStatus(view.current, now) !== 'approved') return { state: 'disabled', reasons: ['Hace falta un formato de autorización aprobado y vigente.'] }
  if (view.circulo.blockers.length) return { state: 'disabled', reasons: view.circulo.blockers }
  if (view.circulo.reusable) return { state: 'reuse', reasons: [] }
  if (latest?.errorCode === 'TIMEOUT_UNKNOWN') return { state: 'with_warning', reasons: [] }
  return { state: 'query', reasons: [] }
}

/** La consulta cuyo reporte se muestra: la encontrada más reciente (una reutilizada trae el de su original). */
export function latestFound(queries: CreditHistoryQuery[]): CreditHistoryQuery | null {
  return queries.find((query) => query.outcome === 'found' && !!query.summary) ?? null
}

/** Totales del reporte: solo cuentas abiertas; las comerciales cuentan para atrasos, no como deuda (D-39). */
export function reportTotals(financial: CreditAccountView[], commercial: CreditAccountView[]) {
  const open = financial.filter((account) => account.isOpen)
  return {
    openAccounts: open.length,
    balance: open.reduce((sum, account) => sum + account.balance, 0),
    pastDue: [...open, ...commercial].reduce((sum, account) => sum + account.pastDue, 0),
    worstDelayDays: Math.max(0, ...[...financial, ...commercial].map((account) => account.worstDelayDays)),
  }
}

/** Las claves de prevención vienen una por contrato: «92 en 9 contratos, 75». */
export function preventionSummary(keys: string[]): string {
  const counts = new Map<string, number>()
  for (const key of keys) counts.set(key, (counts.get(key) ?? 0) + 1)
  return [...counts.entries()].map(([key, count]) => (count > 1 ? `${key} en ${count} contratos` : key)).join(', ')
}
